/**
 * QuizSettingsModal - Settings modal with tabs for live quiz configuration
 * Theme-aware, matches TemplateEditor SettingsTab structure
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '../../context/ThemeContext';

// Tab-Button Component - Theme-aware, Codicons instead of emojis
const TabButton = ({ label, icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={`flex-1 py-3 px-4 text-sm font-medium theme-transition flex items-center justify-center gap-2 ${
      active 
        ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400 bg-gray-50 dark:bg-gray-700/50' 
        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/30'
    }`}
  >
    {icon}
    {label}
  </button>
);

TabButton.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
};

// Checkbox Setting Item - Theme-aware
const SettingCheckbox = ({ checked, onChange, label, description, disabled = false }) => {
  const { isDark } = useTheme();
  return (
    <label className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer theme-transition ${
      isDark 
        ? 'bg-gray-700/50 hover:bg-gray-700' 
        : 'bg-gray-50 hover:bg-gray-100'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-5 h-5 accent-primary-500"
        disabled={disabled}
      />
      <div className="flex-1">
        <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</span>
        {description && (
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {description}
          </p>
        )}
      </div>
    </label>
  );
};

SettingCheckbox.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  disabled: PropTypes.bool
};

// Small Checkbox for grids - Theme-aware
const SmallCheckbox = ({ checked, onChange, label, description, disabled = false }) => {
  const { isDark } = useTheme();
  return (
    <label className={`flex items-start gap-2 p-3 rounded-lg cursor-pointer theme-transition ${
      isDark 
        ? 'bg-gray-700/50 hover:bg-gray-700' 
        : 'bg-gray-50 hover:bg-gray-100'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 accent-primary-500"
        disabled={disabled}
      />
      <div className="flex-1">
        <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</span>
        {description && (
          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {description}
          </p>
        )}
      </div>
    </label>
  );
};

SmallCheckbox.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  disabled: PropTypes.bool
};

// Number Input with label - Theme-aware
const NumberSetting = ({ value, onChange, label, min = 0, disabled = false }) => {
  const { isDark } = useTheme();
  return (
    <div className="flex items-center gap-2">
      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        className={`w-20 p-2 rounded text-center theme-transition ${
          isDark 
            ? 'bg-gray-700 border-gray-600 text-white' 
            : 'bg-white border-gray-300 text-gray-900'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        min={min}
        disabled={disabled}
      />
    </div>
  );
};

NumberSetting.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  min: PropTypes.number,
  disabled: PropTypes.bool
};

/**
 * General Settings Tab
 */
const GeneralTab = ({ settings, updateSettings, instanceSessionCode }) => {
  const { isDark } = useTheme();
  return (
    <div className="space-y-6">
      {/* Display Name */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>Anzeigename</label>
        <input
          type="text"
          value={settings?.displayName || ''}
          onChange={(e) => updateSettings({ displayName: e.target.value })}
          placeholder={instanceSessionCode}
          className={`w-full p-3 rounded-lg border theme-transition ${
            isDark 
              ? 'bg-gray-700 border-gray-600 text-white' 
              : 'bg-white border-gray-300 text-gray-900'
          }`}
        />
        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
          Name, der den Teilnehmern angezeigt wird
        </p>
      </div>

      {/* Participant Mode */}
      <div>
        <label className={`block text-sm font-medium mb-3 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>Teilnehmer-Modus</label>
        <SettingCheckbox
          checked={settings?.anonymousMode ?? false}
          onChange={(checked) => updateSettings({ anonymousMode: checked })}
          label="Anonymes Quiz"
          description="Teilnehmer müssen keinen Namen eingeben"
        />
      </div>
      
      {/* Student Registration Mode */}
      <div>
        <label className={`block text-sm font-medium mb-3 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>Studierenden-Registrierung</label>
        <div className="space-y-2">
          <label className={`flex items-start p-3 rounded-lg cursor-pointer theme-transition border-2 ${
            settings?.studentRegistration === 'none' || !settings?.studentRegistration
              ? isDark 
                ? 'bg-gray-700 border-primary-500' 
                : 'bg-primary-50 border-primary-500'
              : isDark
                ? 'bg-gray-700/50 border-transparent hover:bg-gray-700'
                : 'bg-gray-50 border-transparent hover:bg-gray-100'
          }`}>
          <input
            type="radio"
            name="studentRegistration"
            value="none"
            checked={settings?.studentRegistration === 'none' || !settings?.studentRegistration}
            onChange={(e) => updateSettings({ studentRegistration: e.target.value })}
            className="mt-1 mr-3"
          />
          <div>
            <div className={`font-medium ${isDark ? 'text-white' : 'text-primary-700'}`}>Keine Registrierung</div>
            <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Teilnehmer wählen nur Zufallsnamen</div>
          </div>
        </label>

        <label className={`flex items-start p-3 rounded-lg cursor-pointer theme-transition ${
          settings?.studentRegistration === 'optional'
            ? isDark 
              ? 'bg-gray-700 border-2 border-blue-500' 
              : 'bg-primary-50 border-2 border-primary-500'
            : isDark
              ? 'bg-gray-700/50 border-2 border-transparent hover:bg-gray-700'
              : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
        }`}>
          <input
            type="radio"
            name="studentRegistration"
            value="optional"
            checked={settings?.studentRegistration === 'optional'}
            onChange={(e) => updateSettings({ studentRegistration: e.target.value })}
            className="mt-1 mr-3"
          />
          <div>
            <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Registrierung optional
              <span className="ml-2 text-xs px-2 py-0.5 rounded bg-blue-600 text-white">Empfohlen</span>
            </div>
            <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Link wird angezeigt, Teilnahme als Gast möglich</div>
          </div>
        </label>

        <label className={`flex items-start p-3 rounded-lg cursor-pointer theme-transition ${
          settings?.studentRegistration === 'required'
            ? isDark 
              ? 'bg-gray-700 border-2 border-blue-500' 
              : 'bg-primary-50 border-2 border-primary-500'
            : isDark
              ? 'bg-gray-700/50 border-2 border-transparent hover:bg-gray-700'
              : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
        }`}>
          <input
            type="radio"
            name="studentRegistration"
            value="required"
            checked={settings?.studentRegistration === 'required'}
            onChange={(e) => updateSettings({ studentRegistration: e.target.value })}
            className="mt-1 mr-3"
          />
          <div>
            <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Registrierung verpflichtend</div>
            <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Nur angemeldete Studierende können teilnehmen</div>
          </div>
        </label>
      </div>
    </div>
  </div>
);
};

GeneralTab.propTypes = {
  settings: PropTypes.object,
  updateSettings: PropTypes.func.isRequired,
  instanceSessionCode: PropTypes.string
};

/**
 * Gamification Settings Tab
 */
const GamificationTab = ({ settings, updateSettings }) => {
  const gamificationEnabled = settings?.enableGamification ?? true;
  const timerEnabled = settings?.enableTimer ?? settings?.enableQuestionTimer ?? false;
  const { isDark } = useTheme();
  
  return (
    <div className="space-y-4">
      <SettingCheckbox
        checked={gamificationEnabled}
        onChange={(checked) => updateSettings({ enableGamification: checked })}
        label="Gamification aktivieren"
        description="Punkte, Rangliste und Bonussysteme"
      />

      {/* Timer Settings - affects scoring */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-700/30' : 'bg-gray-100'}`}>
        <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          Timer
        </label>
        <div className="space-y-3">
          <SettingCheckbox
            checked={timerEnabled}
            onChange={(checked) => updateSettings({ 
              enableTimer: checked,
              enableQuestionTimer: checked 
            })}
            label="Fragen-Timer aktivieren"
            description="Zeitlimit pro Frage (beeinflusst Punktevergabe)"
            disabled={!gamificationEnabled}
          />
          
          <div className={`flex items-center gap-3 p-3 rounded-lg theme-transition ${
            isDark ? 'bg-gray-700/50' : 'bg-gray-200'
          }`}>
            <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>Timer-Dauer:</span>
            <input
              type="number"
              value={settings?.timerDuration ?? settings?.questionTimerSeconds ?? 30}
              onChange={(e) => updateSettings({ 
                timerDuration: parseInt(e.target.value) || 30,
                questionTimerSeconds: parseInt(e.target.value) || 30
              })}
              className={`w-24 p-2 border rounded text-center theme-transition ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              min="5"
              disabled={!timerEnabled || !gamificationEnabled}
            />
            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Sekunden</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <SmallCheckbox
          checked={settings?.enableTimeBonus ?? true}
          onChange={(checked) => updateSettings({ enableTimeBonus: checked })}
          label="Zeitbonus"
          description="Schneller = mehr Punkte"
          disabled={!gamificationEnabled}
        />

        <SmallCheckbox
          checked={settings?.enableStreakBonus ?? true}
          onChange={(checked) => updateSettings({ enableStreakBonus: checked })}
          label="Streak-Bonus"
          description="Serie = Extrapunkte"
          disabled={!gamificationEnabled}
        />

        <SmallCheckbox
          checked={settings?.showLeaderboard ?? true}
          onChange={(checked) => updateSettings({ showLeaderboard: checked })}
          label="Rangliste"
          description="Bestenliste anzeigen"
          disabled={!gamificationEnabled}
        />

        <SmallCheckbox
          checked={settings?.showStreak ?? true}
          onChange={(checked) => updateSettings({ showStreak: checked })}
          label="Streak anzeigen"
          description="Aktuelle Serie zeigen"
          disabled={!gamificationEnabled}
        />
      </div>

      <div className={`grid grid-cols-2 gap-3 p-3 rounded-lg theme-transition ${
        isDark ? 'bg-gray-700/30' : 'bg-gray-100'
      } ${!gamificationEnabled ? 'opacity-50' : ''}`}>
        <NumberSetting
          value={settings?.basePointsPerQuestion ?? 100}
          onChange={(val) => updateSettings({ basePointsPerQuestion: val })}
          label="Basispunkte:"
          min={1}
          disabled={!gamificationEnabled}
        />
        <NumberSetting
          value={settings?.streakBonusPoints ?? 50}
          onChange={(val) => updateSettings({ streakBonusPoints: val })}
          label="Streak-Bonus:"
          min={0}
          disabled={!gamificationEnabled || !(settings?.enableStreakBonus ?? true)}
        />
      </div>
    </div>
  );
};

GamificationTab.propTypes = {
  settings: PropTypes.object,
  updateSettings: PropTypes.func.isRequired
};

/**
 * Jokers Settings Tab
 */
const JokersTab = ({ settings, updateSettings }) => {
  const jokersEnabled = settings?.enableJokers ?? false;
  const { isDark } = useTheme();
  
  return (
    <div className="space-y-4">
      <SettingCheckbox
        checked={jokersEnabled}
        onChange={(checked) => updateSettings({ enableJokers: checked })}
        label="Joker aktivieren"
        description="Teilnehmer können Joker einsetzen"
      />

      <div className="grid grid-cols-2 gap-3">
        <SmallCheckbox
          checked={settings?.enableJoker5050 ?? true}
          onChange={(checked) => updateSettings({ enableJoker5050: checked })}
          label="50:50 Joker"
          description="Entfernt 2 falsche Antworten"
          disabled={!jokersEnabled}
        />

        <SmallCheckbox
          checked={settings?.enableJokerAudience ?? true}
          onChange={(checked) => updateSettings({ enableJokerAudience: checked })}
          label="Publikumsjoker"
          description="Zeigt Antworten der anderen"
          disabled={!jokersEnabled}
        />

        <SmallCheckbox
          checked={settings?.enableJokerBestPlayer ?? true}
          onChange={(checked) => updateSettings({ enableJokerBestPlayer: checked })}
          label="Bester-Spieler"
          description="Zeigt Antwort des Besten"
          disabled={!jokersEnabled}
        />

        {/* Zeit-Joker removed - admin now controls timer via Pause/+20s buttons */}
      </div>

      {!jokersEnabled && (
        <p className={`text-sm text-center p-4 rounded-lg theme-transition ${
          isDark ? 'text-gray-500 bg-gray-700/20' : 'text-gray-600 bg-gray-100'
        }`}>
          Aktiviere "Joker aktivieren" um die einzelnen Joker zu konfigurieren
        </p>
      )}
    </div>
  );
};

JokersTab.propTypes = {
  settings: PropTypes.object,
  updateSettings: PropTypes.func.isRequired
};

/**
 * Behavior Settings Tab
 */
const BehaviorTab = ({ settings, updateSettings }) => (
  <div className="space-y-4">
    <SettingCheckbox
      checked={settings?.lockOnReveal ?? true}
      onChange={(checked) => updateSettings({ lockOnReveal: checked })}
      label="Auto-Sperren bei Auflösung"
      description="Antworten automatisch sperren wenn richtige Antwort gezeigt wird"
    />

    <SettingCheckbox
      checked={settings?.autoAwardPointsOnNext ?? false}
      onChange={(checked) => updateSettings({ autoAwardPointsOnNext: checked })}
      label="Punkte automatisch vergeben"
      description="Punkte automatisch vergeben wenn zur nächsten Frage gewechselt wird"
    />

    <SettingCheckbox
      checked={settings?.showIntroScreen ?? true}
      onChange={(checked) => updateSettings({ showIntroScreen: checked })}
      label="Intro-Bildschirm anzeigen"
      description="Willkommensseite vor Quiz-Start"
    />

    {/* DISABLED: Not persisting correctly */}
    {/* <SettingCheckbox
      checked={settings?.showResultsAfterAnswer ?? false}
      onChange={(checked) => updateSettings({ showResultsAfterAnswer: checked })}
      label="Ergebnisse nach Antwort"
      description="Sofort Ergebnisse anzeigen nach Abgabe"
    /> */}

    <SettingCheckbox
      checked={settings?.shuffleQuestions ?? false}
      onChange={(checked) => updateSettings({ shuffleQuestions: checked })}
      label="Fragen mischen"
      description="Zufällige Reihenfolge für jeden Teilnehmer"
    />

    {/* DISABLED: Not persisting correctly */}
    {/* <SettingCheckbox
      checked={settings?.shuffleOptions ?? false}
      onChange={(checked) => updateSettings({ shuffleOptions: checked })}
      label="Antworten mischen"
      description="Zufällige Reihenfolge der Antwortoptionen"
    /> */}
  </div>
);

BehaviorTab.propTypes = {
  settings: PropTypes.object,
  updateSettings: PropTypes.func.isRequired
};

/**
 * Main QuizSettingsModal Component
 */
function QuizSettingsModal({ 
  isOpen, 
  onClose, 
  onSave, 
  settings, 
  updateSettings,
  instanceSessionCode 
}) {
  const [activeTab, setActiveTab] = useState('general');

  if (!isOpen || !settings) return null;

  const tabs = [
    { 
      key: 'general', 
      label: 'Allgemein', 
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    },
    { 
      key: 'gamification', 
      label: 'Gamification', 
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    },
    { 
      key: 'jokers', 
      label: 'Joker', 
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    },
    { 
      key: 'behavior', 
      label: 'Verhalten', 
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    }
  ];

  const { isDark } = useTheme();
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className={`rounded-xl w-full max-w-3xl max-h-[90vh] shadow-2xl flex flex-col theme-transition ${
        isDark ? 'bg-gray-800' : 'bg-white'
      }`} onClick={e => e.stopPropagation()}>
        {/* Header - Fixed Height */}
        <div className="flex justify-between items-center px-4 py-3 h-[60px] shrink-0 border-b theme-transition ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }">
          <div className="flex items-center gap-2">
            <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className={`text-lg font-bold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>Quiz-Einstellungen</span>
          </div>
          <button 
            onClick={onClose} 
            className={`p-2 rounded-lg theme-transition ${
              isDark 
                ? 'hover:bg-gray-700 text-gray-400 hover:text-white' 
                : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex shrink-0 border-b theme-transition ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          {tabs.map(tab => (
            <TabButton
              key={tab.key}
              label={tab.label}
              icon={tab.icon}
              active={activeTab === tab.key}
              onClick={() => setActiveTab(tab.key)}
            />
          ))}
        </div>
        
        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'general' && (
            <GeneralTab 
              settings={settings} 
              updateSettings={updateSettings}
              instanceSessionCode={instanceSessionCode}
            />
          )}
          {activeTab === 'gamification' && (
            <GamificationTab settings={settings} updateSettings={updateSettings} />
          )}
          {activeTab === 'jokers' && (
            <JokersTab settings={settings} updateSettings={updateSettings} />
          )}
          {activeTab === 'behavior' && (
            <BehaviorTab settings={settings} updateSettings={updateSettings} />
          )}
        </div>
        
        {/* Footer with Buttons */}
        <div className={`flex justify-end gap-3 p-4 shrink-0 border-t theme-transition ${
          isDark 
            ? 'border-gray-700 bg-gray-800/50' 
            : 'border-gray-200 bg-gray-50'
        }`}>
          <button
            onClick={onClose}
            className={`px-5 py-2.5 rounded-lg theme-transition ${
              isDark 
                ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
            }`}
          >
            Abbrechen
          </button>
          <button
            onClick={onSave}
            className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 rounded-lg theme-transition font-medium flex items-center gap-2 text-white"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

QuizSettingsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  settings: PropTypes.object,
  updateSettings: PropTypes.func.isRequired,
  instanceSessionCode: PropTypes.string
};

export default QuizSettingsModal;
